// Auto-Mix ("Auto-VJ") - automatic, musically-timed variation between audio-reactive effects.
//
// The point of this module is that a live set shouldn't sit on one look for minutes at a time.
// Rather than a dumb timer, scene changes are quantised to detected beats and only happen once a
// minimum dwell time has passed, so a switch always lands ON the music instead of cutting across
// it. Each scene is a small curated combination (a motion effect + a colour effect) that is known
// to look good together, which avoids the muddy result of stacking arbitrary effects at random.

import { state } from "./state.js";

// Every field Auto-Mix is allowed to drive. Anything the user switches on manually outside this
// list (morph, permanent colours, wrap mode, ...) is deliberately left alone.
const MANAGED_FIELDS = [
  "bioluminescenceOn",
  "spectrumColorOn",
  "chromaWaveOn",
  "beatFlashOn",
  "audioSizeOn",
  "beatPulseOn",
  "breathOn",
  "tunnelOn",
  "helixOn",
  "strobeFieldOn",
  "shimmerOn",
  "spiralHighOn",
  "bandExplodeOn",
  "complexOn",
  "scalePulseOn",
  "colorMidOn",
  "colorInvertOn",
  "patternOn",
  "shapeChangeOn",
  "vortexOn",
  "galaxySpiralOn",
  "galaxyDiscOn",
  "nebulaOn",
  "swarmOn",
  "portalOn",
  "causticsOn",
  "liquidRippleOn",
  "emberOn",
  "starSkyOn",
];

// Curated scenes: each is a set of fields to enable together. Kept to 2-4 effects so the result
// stays readable on a projector instead of turning into noise.
const SCENES = [
  { name: "Biolumineszenz", fields: ["bioluminescenceOn", "breathOn", "audioSizeOn"] },
  { name: "Spektrum-Schwarm", fields: ["spectrumColorOn", "swarmOn", "audioSizeOn"] },
  { name: "Chroma-Vortex", fields: ["chromaWaveOn", "vortexOn"] },
  { name: "Beat-Galaxie", fields: ["beatFlashOn", "beatPulseOn", "galaxySpiralOn"] },
  { name: "Tunnelflug", fields: ["tunnelOn", "chromaWaveOn", "audioSizeOn"] },
  { name: "DNA-Helix", fields: ["helixOn", "spectrumColorOn"] },
  { name: "Strobe-Fraktur", fields: ["strobeFieldOn", "colorInvertOn", "beatFlashOn"] },
  { name: "Nebel-Traum", fields: ["nebulaOn", "bioluminescenceOn", "breathOn"] },
  { name: "Sternenfeld", fields: ["starSkyOn", "shimmerOn", "audioSizeOn"] },
  { name: "Kaustik-Wasser", fields: ["causticsOn", "liquidRippleOn", "chromaWaveOn"] },
  { name: "Glut-Sturm", fields: ["emberOn", "beatPulseOn", "colorMidOn"] },
  { name: "Portal-Puls", fields: ["portalOn", "beatFlashOn", "scalePulseOn"] },
  { name: "Scheibe & Schimmer", fields: ["galaxyDiscOn", "shimmerOn", "spectrumColorOn"] },
  { name: "Muster-Morph", fields: ["patternOn", "chromaWaveOn", "audioSizeOn"] },
  { name: "Hochfrequenz-Spirale", fields: ["spiralHighOn", "shimmerOn", "beatFlashOn"] },
  { name: "Band-Chaos", fields: ["bandExplodeOn", "spectrumColorOn", "beatPulseOn"] },
];

export class AutoMix {
  constructor() {
    this.sceneIndex = -1;
    this.currentSceneName = "";
    this._sinceSwitch = 0;
    this._wasOn = false;
    this._recent = [];
  }

  // Minimum seconds a scene is held. autoMixIntensity 0 -> long, contemplative scenes;
  // 1 -> fast, aggressive cutting.
  get _dwellTime() {
    const t = state.autoMixIntensity;
    return 22 - t * 17; // 22s .. 5s
  }

  update(dt, audio) {
    if (!state.autoMixOn) {
      if (this._wasOn) {
        // Leaving auto mode: clear whatever the automation switched on so the user gets a clean
        // slate back rather than inheriting a random scene.
        this._clearManaged();
        this._wasOn = false;
        this.currentSceneName = "";
        this.sceneIndex = -1;
      }
      return;
    }

    if (!this._wasOn) {
      this._wasOn = true;
      this._sinceSwitch = Infinity; // force an immediate first scene
    }

    this._sinceSwitch += dt;

    if (this._sinceSwitch < this._dwellTime) return;
    // Past the dwell time: wait for the next beat so the cut lands on the music. If the track is
    // quiet/beatless, fall back to switching anyway after a generous grace period.
    if (!audio.beat && this._sinceSwitch < this._dwellTime * 1.8) return;

    this._nextScene();
  }

  _nextScene() {
    // Pick a scene we haven't used recently, so a short set doesn't visibly loop.
    let candidate = 0;
    for (let attempt = 0; attempt < 12; attempt++) {
      candidate = Math.floor(Math.random() * SCENES.length);
      if (!this._recent.includes(candidate)) break;
    }
    this._recent.push(candidate);
    if (this._recent.length > Math.min(6, SCENES.length - 2)) this._recent.shift();

    this._clearManaged();
    const scene = SCENES[candidate];
    for (const field of scene.fields) state[field] = true;

    this.sceneIndex = candidate;
    this.currentSceneName = scene.name;
    this._sinceSwitch = 0;
  }

  _clearManaged() {
    for (const field of MANAGED_FIELDS) state[field] = false;
  }
}
